import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Logo from "../../../../assets/logo.png";
import {
  FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram, FaYoutube,
} from "react-icons/fa";
import "./Footer.css";

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const SEEKERS = [
  { label: "Browse Jobs",       to: "/employee/jobs",              badge: null },
  { label: "Career Guidance",   to: "/employee/career-guidance",   badge: null },
  { label: "Resume Builder",    to: "/employee/resume-builder",    badge: "Popular" },
  { label: "ATS Score Checker", to: "/employee/ats-score-checker", badge: null },
  { label: "Salary Guide",      to: "/employee/salary-guide",      badge: "New" },
  { label: "Earn Rewards",      to: "/employer/rewards",           badge: null },
];
const EMPLOYERS = [
  { label: "Post a Job",            to: "/employer/signup",               badge: null },
  { label: "Resume Database",       to: "/employer/recruiter-dashboard",  badge: null },
  { label: "Recruitment Solutions", to: "/employer",                      badge: null },
  { label: "Pricing Plans",         to: "/employer",                      badge: null },
  { label: "Analytics Dashboard",   to: "/employer",                      badge: null },
  { label: "Employer Rewards",      to: "/employer/rewards",              badge: null },
];
const TOOLS = [
  { label: "Salary Calculator",   to: "/employee/salary-calculator",    badge: null },
  { label: "HRA Calculator",      to: "/employee/hra-calculator",       badge: null },
  { label: "EPF Calculator",      to: "/employee/pf-calculator",        badge: null },
  { label: "NPS Calculator",      to: "/employee/nps-calculator",       badge: null },
  { label: "PPF Calculator",      to: "/employee/ppf-calculator",       badge: null },
  { label: "Tax Calculator",      to: "/employee/tax-calculator",       badge: null },
  { label: "Retirement Planner",  to: "/employee/retirement-calculator",badge: null },
];
const COMPANY = [
  { label: "About Us",        to: "/employee/about",         badge: null },
  { label: "Report Fraud",    to: "/employee/report-fraud",  badge: null },
  { label: "Relocate Hub",    to: "/employee/relocate-hub",  badge: null },
  { label: "Help Center",     to: "/employee/contact",       badge: null },
  { label: "Terms & Policy",  to: "/employee/terms&policy",  badge: null },
];
const SOCIALS = [
  { Icon: FaFacebookF,  label: "Facebook",  href: "#" },
  { Icon: FaTwitter,    label: "Twitter",   href: "#" },
  { Icon: FaLinkedinIn, label: "LinkedIn",  href: "#" },
  { Icon: FaInstagram,  label: "Instagram", href: "#" },
  { Icon: FaYoutube,    label: "YouTube",   href: "#" },
];


/* ─────────────────────────────────────────────
   INTERSECTION HOOK
───────────────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); io.disconnect(); } },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ─────────────────────────────────────────────
   ANIMATED COUNTER
───────────────────────────────────────────── */
function Counter({ target, suffix = "" }) {
  const [val, setVal] = useState(0);
  const [ref, inView] = useInView(0.3);
  useEffect(() => {
    if (!inView) return;
    const num = parseFloat(target.replace(/[^0-9.]/g, ""));
    const isFloat = target.includes(".");
    let start = 0;
    const steps = 40;
    const inc = num / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      start = Math.min(start + inc, num);
      setVal(isFloat ? start.toFixed(1) : Math.floor(start));
      if (step >= steps) clearInterval(timer);
    }, 28);
    return () => clearInterval(timer);
  }, [inView, target]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ─────────────────────────────────────────────
   LINK COLUMN
───────────────────────────────────────────── */
function FCol({ title, links, delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`thf-col ${inView ? "thf-col--in" : ""}`}
      style={{ "--delay": `${delay}ms` }}
    >
      <p className="thf-col-title">
        <span className="thf-col-title-line" />
        {title}
      </p>
      <nav className="thf-link-list">
        {links.map((l, i) => (
          <Link
            key={l.label}
            to={l.to}
            className="thf-link"
            style={{ "--li": i }}
          >
            <span className="thf-link-dot" />
            <span className="thf-ltext">{l.label}</span>
            {l.badge === "Popular" && <span className="thf-pop">Popular</span>}
            {l.badge === "New"     && <span className="thf-new">New</span>}
            <span className="thf-arr">›</span>
          </Link>
        ))}
      </nav>
    </div>
  );
}

/* ─────────────────────────────────────────────
   NEWSLETTER
───────────────────────────────────────────── */
function Newsletter() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email.includes("@")) return;
    setBusy(true);
    setTimeout(() => { setBusy(false); setDone(true); }, 900);
  };

  return (
    <div className="thf-nl">
      <div className="thf-nl-header">
        <span className="thf-nl-icon">✉️</span>
        <div>
          <p className="thf-nl-title">Career Alerts & Job Tips</p>
          <p className="thf-nl-sub">Weekly digest · No spam, ever</p>
        </div>
      </div>
      {done ? (
        <div className="thf-nl-ok">
          <span>🎉</span> You're in! Check your inbox for a welcome mail.
        </div>
      ) : (
        <form className="thf-nl-form" onSubmit={handleSubmit}>
          <input
            className="thf-nl-input"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button className="thf-nl-btn" type="submit" disabled={busy}>
            {busy ? <span className="thf-nl-spin" /> : "Subscribe →"}
          </button>
        </form>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN FOOTER
───────────────────────────────────────────── */
export default function Footer() {
  const [statsRef, statsIn] = useInView(0.2);

  return (
    <footer className="thf-root">

      {/* ══ MARQUEE TICKER ══ */}
      <div className="thf-ticker" aria-hidden="true">
        <div className="thf-ticker-track">
          {[
            "🏢 9M+ Professionals",
            "💼 2.4L+ Active Jobs",
            "🎯 85K+ Companies",
            "⭐ 4.8 App Rating",
            "🚀 India's #1 Job Portal",
            "🎓 Free Career Guidance",
            "🔥 New Jobs Every Hour",
          ].concat([
            "🏢 9M+ Professionals",
            "💼 2.4L+ Active Jobs",
            "🎯 85K+ Companies",
            "⭐ 4.8 App Rating",
            "🚀 India's #1 Job Portal",
            "🎓 Free Career Guidance",
            "🔥 New Jobs Every Hour",
          ]).map((item, i) => (
            <span key={i} className="thf-ticker-item">{item}</span>
          ))}
        </div>
      </div>



      {/* ══ MAIN ROW ══ */}
      <div className="thf-main">

        {/* ── Brand panel ── */}
        <div className="thf-brand">
          <div className="thf-logo-wrap">
            <img src={Logo} alt="TalentHive" className="thf-logo" />
          </div>

          <p className="thf-tagline">
            India's smartest hiring &amp; career intelligence platform —
            connecting <strong>talent</strong> with <strong>opportunity</strong>.
          </p>

          <div className="thf-trust-row">
            {["ISO Certified", "GDPR Safe", "256-bit SSL"].map((t) => (
              <span key={t} className="thf-trust-chip">
                <span className="thf-trust-dot" />
                {t}
              </span>
            ))}
          </div>

          <div className="thf-socials">
            {SOCIALS.map(({ Icon, label, href }) => (
              <a key={label} href={href} aria-label={label} className="thf-social">
                <Icon size={13} />
              </a>
            ))}
          </div>

          {/* <Newsletter /> */}

          <Link to="/employee/jobs" className="thf-cta-btn">
            Explore 2.4L+ Jobs →
          </Link>
        </div>

        {/* ── Link columns ── */}
        <div className="thf-cols">
          <FCol title="Job Seekers" links={SEEKERS}   delay={0}   />
          <FCol title="Employers"   links={EMPLOYERS} delay={60}  />
          <FCol title="Calculators" links={TOOLS}     delay={120} />
          <FCol title="Company"     links={COMPANY}   delay={180} />
        </div>
      </div>



      {/* ══ BOTTOM BAR ══ */}
      <div className="thf-bottom">
        <span className="thf-copy">
          © 2026 <strong>TalentHive</strong> by Indian Hospitality Network. Made with ❤️ for India's workforce.
        </span>
        <div className="thf-legal">
          {["Privacy", "Terms", "Cookies", "Sitemap", "Accessibility"].map((l) => (
            <a key={l} href="#" className="thf-la">{l}</a>
          ))}
        </div>
      </div>

    </footer>
  );
}